/**
 * Social page CMS copy + curated gallery config.
 *
 * Hard rules baked into this file:
 *   - Brand caps: `Pluck`, `Pluckers`, `Plucking`, `PooPartner`,
 *     `PooPluckers`, `Pluckin'` (with apostrophe) are ALWAYS
 *     capitalised. Pre-commit grep (`[a-z]pluck`, `[a-z]lucker`)
 *     must return zero hits here.
 *   - Curated-only gallery this dispatch — NO user-generated
 *     content, NO live social scraping/embed, NO DB schema. Photos
 *     are admin-supplied via the `SOCIAL_GALLERY.photos` array
 *     below. Inclusion in the array implies admin approval; a
 *     future DB-backed `approved=true` filter is a non-breaking
 *     migration (the gallery component contract stays the same).
 *   - Honesty constraint: every "coming soon" / "launching with
 *     our first neighborhoods" string reflects a real product
 *     gate. Never decorate with fake countdowns or fabricated
 *     pre-launch metrics.
 *
 * Scope guardrail: this module DOES NOT preempt the full social-
 * platform deliverable described in
 * `/app/memory/social_step0_investigation.md` (3 new DB collections,
 * UGC posts, moderation pipeline, object-storage avatars). When that
 * platform ships, this curated gallery can either become a
 * "featured photos" sub-section or get demoted to a different
 * route — the contract here is intentionally narrow.
 */
export const COMMUNITY_HERO = {
  eyebrow: "PooPluckers community",
  // Single-line headline (no accent split) since the page's current
  // copy is a complete sentence. Kept verbatim from the prior
  // ComingSoon placeholder so we preserve user-sanctioned wording.
  headline: "The social feed is coming soon.",
  intro:
    "A friendly, opt-in space where local PooPartners and dog owners can share tips and wins. Moderation-first \u2014 launching with our first neighborhoods.",
};

/**
 * Fallback hero for `/social/just-plucking-around` when the Elfsight
 * widget env var is absent. The Elfsight widget IS the primary hero
 * when configured; this band only renders when the widget is not
 * wired so the page always has a top-of-page identity.
 */
export const JPA_HERO = {
  eyebrow: "Just Plucking Around",
  headline: "The pack, live \u2014 coming soon.",
  intro:
    "Follow #PooPluckers on your favorite platform to see snapshots as they roll in. Tag your dog's clean-yard moment with #PooPluckers to be featured here.",
};

// Legacy export retained so any lingering importer (e.g. tests
// that pre-date the split) keeps compiling. New code should read
// `COMMUNITY_HERO` (or `JPA_HERO` for the live-page fallback).
export const SOCIAL_HERO = COMMUNITY_HERO;

export const SOCIAL_GALLERY = {
  section_kick: "Pluckin\u2019 Around",
  section_subhead:
    "Snapshots from PooPluckers across the neighborhood. Admin-curated, refreshed as our first neighborhoods come online.",
  // Exact wording locked by spec — do not soften, do not add a CTA.
  empty_state_copy: "Pluckin\u2019 Around \u2014 photos coming soon.",
  /**
   * Photo entry shape:
   *   {
   *     id: string,          // stable React key + lightbox index
   *     src: string,         // path under public/ (e.g. "/social/photo-001.jpg")
   *     webp?: string,       // optional webp variant for <picture> source
   *     alt: string,         // REQUIRED — descriptive alt text
   *     caption?: string,    // optional caption rendered under photo + in lightbox
   *     credit?: string,     // optional attribution ("Photo by PooPartner Jane")
   *     aspect?: "portrait" | "landscape" | "square",  // layout hint
   *   }
   *
   * Empty array = empty-state card renders. Admin drops new entries
   * here (and assets under /app/frontend/public/social/) to populate.
   */
  photos: [],
};

/**
 * "Follow us" social-platform links rendered on `/social` (and, when the
 * footer-social sub-component lands, reused there too — see
 * `analytics_pluckonomics_plan.md` follow-up backlog).
 *
 * Pure data — `iconName` is a string keyed against an `ICON_MAP` inside
 * the consuming component (Social.jsx). Keeps THIS file dependency-free
 * of `lucide-react` so future Instagram adoption is a 1-line uncomment
 * with no import-graph changes.
 *
 * Entry shape (frozen):
 *   {
 *     platform:  string,   // display name + key
 *     url:       string,   // absolute https URL (opens in new tab)
 *     iconName:  string,   // ICON_MAP key in Social.jsx ("Facebook" | ...)
 *     ariaLabel: string,   // accessible name for screen readers
 *     testId:    string,   // stable data-testid suffix
 *   }
 *
 * To add Instagram once the handle is registered:
 *   - Confirm the brand caps (`PooPluckers`, NOT `pooppluckers`).
 *   - Uncomment the Instagram block below.
 *   - Ensure ICON_MAP in Social.jsx has the matching key (already wired
 *     for "Instagram" — lucide-react ships the icon component).
 *   - No other code change required — the `.map()` renders it.
 */
export const SOCIAL_LINKS = [
  {
    platform: "Facebook",
    url: "https://www.facebook.com/PooPluckers",
    iconName: "Facebook",
    ariaLabel: "PooPluckers on Facebook",
    testId: "social-follow-link-facebook",
  },
  {
    platform: "Instagram",
    // Brand-caps EXCEPTION: Instagram handle URLs are case-sensitive at
    // the platform layer and the real PooPluckers handle is registered
    // as lowercase `poopluckers`. Display copy (aria-label, tooltips)
    // keeps the brand caps `PooPluckers` per house style — only the
    // external URL path stays lowercase. The domain follows a related
    // (but not identical) rule per the 2026-07-20 dispatch: DISPLAY as
    // `PooPluckers.com` (title case; lowercase misparses as
    // "poop-luckers"), but URLs / hrefs / og:url / canonical all stay
    // lowercase because DNS is case-insensitive and rewriting them
    // touches too many downstream consumers. Do NOT "fix" the
    // Instagram URL path to title case; the link will 404. Facebook
    // URL is the inverse case — Facebook page slugs are
    // case-insensitive so `/PooPluckers` resolves fine.
    url: "https://www.instagram.com/poopluckers/",
    iconName: "Instagram",
    ariaLabel: "PooPluckers on Instagram",
    testId: "social-follow-link-instagram",
  },
];

export const SOCIAL_FOLLOW_HEADING = {
  eyebrow: "Follow PooPluckers",
  copy: "Tips, neighborhood wins, and the occasional dog selfie.",
};
